import React from 'react';
import { motion } from 'framer-motion';
import { CrownIcon, XIcon } from 'lucide-react';
import { ProductName } from './ProductName';
import { FlowLine } from './FlowLine';
import { Ornament } from './Ornament';
const notThis = ['This is not a random collection of Barre exercises.', 'This is not another text-heavy fitness manual that gives you theory but leaves you responsible for building the actual class.', 'And it is not a folder full of disconnected routines you still have to organize yourself.'];
const builtAround = ['ready-to-teach classes', 'movement sequences', 'body positioning', 'directional arrows', 'class timelines', 'muscle focus', 'modifications', 'progressions', 'coaching cues', 'common errors', 'visual corrections', 'equipment variations', 'beginner-to-advanced class development'];
const usageSteps = ['Open the system.', 'Choose the class focus.', 'See the sequence and progression.', 'Teach.'];
export function SolutionReveal() {
  return <section className="bg-inverse px-4 py-20 text-inverse-foreground sm:py-28" data-id="element-303" data-mp="2bunpnjjb0a3n">
      <div className="mx-auto max-w-4xl text-center" data-id="element-304" data-mp="2guademwrd8qp">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-305" data-mp="2gwwlqorh3s9z">
          <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full border border-accent/60 text-accent" data-id="element-306" data-mp="1t57octzsodm6">
            <CrownIcon size={24} strokeWidth={1.5} aria-hidden="true" data-id="element-307" data-mp="1x7uk35zl6ion" />
          </span>
          <p className="mt-5 text-sm font-semibold uppercase tracking-[0.18em] text-inverse-foreground/70" data-id="element-308" data-mp="225e0ocjrb7jr">
            The complete <span className="text-accent-soft" data-id="element-309" data-mp="26xe4ig2y6klm">6-in-1</span> visual
            teaching system for Barre instructors
          </p>
          <h2 className="mt-4 text-3xl font-semibold leading-tight text-inverse-foreground sm:text-5xl" data-id="element-310" data-mp="19f5p53jq7le5">
            <ProductName script accentClassName="text-accent-soft" data-id="element-311" data-mp="1edyqjxlqnlxy" />
          </h2>
          <Ornament className="mt-5" data-id="element-312" data-mp="2hpe9rqnktqhg" />
          <h3 className="mx-auto mt-5 max-w-2xl text-2xl font-medium italic text-inverse-foreground" data-id="element-313" data-mp="2cse075riq5ll">
            Stop Building Every Barre Class From Scratch
          </h3>
          <p className="mx-auto mt-4 max-w-3xl text-sm font-medium uppercase leading-7 tracking-[0.08em] text-inverse-foreground/70" data-id="element-314" data-mp="2qlnad8naiv56">
            6 Complete Instructor Volumes · 150 Ready-to-Teach Classes · 100%
            Visual Core System · 5 Professional Bonus Guides
          </p>
        </motion.div>

        <div className="mt-12 grid gap-5 text-left md:grid-cols-2" data-id="element-315" data-mp="1tvmj92zg82kp">
          <div className="space-y-4 self-start rounded-xl border border-inverse-foreground/10 bg-inverse-foreground/[0.04] p-6" data-id="element-316" data-mp="27um7wamyohgv">
            {notThis.map(item => <div key={item} className="flex gap-3 text-sm leading-6 text-inverse-foreground/75" data-id="element-317" data-mp="3j7ewxdodart8">
                <XIcon size={18} className="mt-0.5 shrink-0 text-accent-soft" aria-hidden="true" data-id="element-318" data-mp="3eb7qzkvz1gba" />
                {item}
              </div>)}
          </div>
          <div className="rounded-xl border border-accent/40 bg-inverse-raised p-6" data-id="element-319" data-mp="3cshheg9wk0ur">
            <p className="text-base font-medium leading-7 text-inverse-foreground" data-id="element-320" data-mp="3hqsrms440ddi">
              Barre Instructor Ready is a complete 6-in-1 teaching system
              created specifically for instructors who want to plan less,
              bring more variety into their classes and teach with a clearer
              structure.
            </p>
            <p className="mt-4 text-sm font-semibold text-accent-soft" data-id="element-321" data-mp="3vqalvn06ciu9">
              The six-volume core system is built around:
            </p>
            <ul className="mt-3 flex flex-wrap gap-1.5" data-id="element-322" data-mp="sadbpe20javx">
              {builtAround.map(item => <li key={item} className="rounded-full border border-accent/30 bg-inverse-foreground/5 px-2.5 py-1 text-xs font-medium text-inverse-foreground" data-id="element-323" data-mp="x0jyr9gvmnc2">
                  {item}
                </li>)}
            </ul>
            <p className="mt-4 text-sm leading-6 text-inverse-foreground/70" data-id="element-324" data-mp="2vjow398teoqo">
              And the core material is 100% visual.
            </p>
            <FlowLine tone="dark" className="mt-5" label="So instead of fighting through pages of unnecessary theory, you can:" steps={['See it', 'Understand it', 'Teach it']} data-id="element-325" data-mp="2qlx6ilvn7epn" />
            <ol className="mt-5 space-y-2.5" data-id="element-326" data-mp="3eiktetfyzj8q">
              {usageSteps.map((step, index) => <li key={step} className="flex items-start gap-3 text-sm leading-6 text-inverse-foreground" data-id="element-327" data-mp="39jhn75o976z6" data-mpi={index}>
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground" data-id="element-328" data-mp="av2avbwtjjhl">
                    {index + 1}
                  </span>
                  {step}
                </li>)}
            </ol>
          </div>
        </div>
      </div>
    </section>;
}